import React, { useState, useEffect, useRef } from "react";
import "./Table.css";

interface TableProps {
  headers: string[];
  data: Record<string, React.ReactNode>[];
  onSelectRows?: (selected: Record<string, React.ReactNode>[]) => void;
  isRowSelectable?: (row: Record<string, React.ReactNode>) => boolean;
  preSelectedRows?: Record<string, React.ReactNode>[];
}

function Table({
  headers,
  data,
  onSelectRows,
  isRowSelectable,
  preSelectedRows = [],
}: TableProps) {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const initialSelectionDone = useRef(false);

  useEffect(() => {
    if (!initialSelectionDone.current && preSelectedRows.length > 0) {
      const initialSelectedIndices = preSelectedRows
        .map((preSelectedRow) =>
          data.findIndex((row) => row.uuid === preSelectedRow.uuid)
        )
        .filter((index) => index !== -1);
      setSelectedRows(initialSelectedIndices);
      initialSelectionDone.current = true;
    }
  }, [data, preSelectedRows]);

  const handleRowSelect = (index: number) => {
    if (isRowSelectable && !isRowSelectable(data[index])) {
      return;
    }

    let updatedSelection: number[];
    if (selectedRows.includes(index)) {
      updatedSelection = selectedRows.filter((i) => i !== index);
    } else {
      updatedSelection = [...selectedRows, index];
    }
    setSelectedRows(updatedSelection);

    if (onSelectRows) {
      onSelectRows(data.filter((_, i) => updatedSelection.includes(i)));
    }
  };

  const handleSelectAll = () => {
    const selectableRows = data.filter(
      (row) => !isRowSelectable || isRowSelectable(row)
    );
    if (selectedRows.length === selectableRows.length) {
      setSelectedRows([]);
      if (onSelectRows) onSelectRows([]);
    } else {
      const selectableIndices = data
        .map((row, i) => (isRowSelectable && !isRowSelectable(row) ? -1 : i))
        .filter((i) => i !== -1);
      setSelectedRows(selectableIndices);
      if (onSelectRows) {
        onSelectRows(data.filter((_, i) => selectableIndices.includes(i)));
      }
    }
  };

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>
            <input
              type="checkbox"
              onChange={handleSelectAll}
              checked={
                selectedRows.length > 0 &&
                selectedRows.length ===
                  data.filter((row) => !isRowSelectable || isRowSelectable(row))
                    .length
              }
            />
          </th>
          {headers.map((header, index) => (
            <th key={index}>
              <span>{header}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, index) => (
          <tr key={index}>
            <td>
              <input
                type="checkbox"
                checked={selectedRows.includes(index)}
                onChange={() => handleRowSelect(index)}
                disabled={isRowSelectable && !isRowSelectable(row)}
              />
            </td>
            {headers.map((header, i) => (
              <td key={i}>
                <span>{row[header]}</span>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default Table;
